import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

export default function CGUPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SeoMeta
        title="Conditions Générales d'Utilisation"
        description="CGU du service Drone Maps : accès et utilisation de la plateforme SaaS de conformité réglementaire drone en Europe (EU 2019/947)."
        keywords="CGU Drone Maps, conditions utilisation drone, SaaS drone Europe, EU 2019/947"
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
        
        <h1 className="text-3xl font-bold text-slate-900 mb-8 font-[Barlow]">Conditions Générales d'Utilisation</h1>
        
        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Objet</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir les modalités 
              d'accès et d'utilisation du service Drone Maps, accessible à l'adresse <strong>drone-maps.eu</strong>, 
              édité par BOD CORP.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Description du service</h2>
            <p className="text-slate-600 leading-relaxed">
              Drone Maps est une plateforme SaaS (Software as a Service) destinée aux opérateurs de drones professionnels 
              et amateurs en Europe. Le service propose :<br /><br />
              • Analyse réglementaire des zones de vol selon la réglementation européenne EU 2019/947<br />
              • Cartographie interactive avec superposition des zones aériennes réglementées<br />
              • Génération de rapports de mission PDF<br />
              • Détection automatique des contraintes (zones urbaines, espaces aériens, zones naturelles protégées)<br />
              • Recommandations pour la mise en conformité réglementaire
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Acceptation des CGU</h2>
            <p className="text-slate-600 leading-relaxed">
              L'utilisation du service Drone Maps implique l'acceptation pleine et entière des présentes CGU. 
              En créant un compte ou en utilisant le service, vous reconnaissez avoir lu, compris et accepté 
              l'ensemble de ces conditions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Inscription et compte utilisateur</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour accéder au service, vous devez créer un compte utilisateur en fournissant des informations 
              exactes et à jour. Vous êtes responsable de la confidentialité de vos identifiants de connexion 
              et de toutes les activités effectuées depuis votre compte.<br /><br />
              Vous vous engagez à :<br />
              • Fournir des informations véridiques lors de l'inscription<br />
              • Maintenir la confidentialité de votre mot de passe<br />
              • Notifier immédiatement toute utilisation non autorisée de votre compte<br />
              • Ne pas créer plusieurs comptes pour une même personne
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Utilisation du service</h2>
            <p className="text-slate-600 leading-relaxed">
              Vous vous engagez à utiliser Drone Maps de manière conforme à la loi et aux présentes CGU. 
              Il est notamment interdit de :<br /><br />
              • Utiliser le service à des fins illégales ou non autorisées<br />
              • Tenter de contourner les mesures de sécurité du service<br />
              • Extraire ou réutiliser de manière systématique les données du service<br />
              • Partager votre compte avec des tiers<br />
              • Utiliser des robots ou scripts automatisés pour accéder au service
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Limitation de responsabilité</h2>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-amber-800 font-medium">⚠️ Avertissement important</p>
            </div>
            <p className="text-slate-600 leading-relaxed">
              <strong>Drone Maps fournit des informations à titre indicatif uniquement.</strong><br /><br />
              Les informations réglementaires, cartes et analyses fournies par Drone Maps ne se substituent en aucun cas 
              aux publications officielles (AIP, NOTAM, réglementations nationales et locales). L'utilisateur reste 
              seul responsable de la vérification des informations auprès des autorités compétentes avant tout vol.<br /><br />
              BOD CORP ne saurait être tenue responsable :<br />
              • Des erreurs ou omissions dans les données fournies<br />
              • Des décisions prises sur la base des informations du service<br />
              • Des dommages directs ou indirects résultant de l'utilisation du service<br />
              • Des interruptions temporaires du service
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Propriété intellectuelle</h2>
            <p className="text-slate-600 leading-relaxed">
              L'ensemble des éléments du service Drone Maps (interface, textes, logos, algorithmes, bases de données) 
              sont la propriété exclusive de BOD CORP et sont protégés par les lois sur la propriété intellectuelle.<br /><br />
              L'utilisateur dispose d'un droit d'utilisation personnel, non exclusif et non transférable du service, 
              dans le cadre de son abonnement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Données personnelles</h2>
            <p className="text-slate-600 leading-relaxed">
              La collecte et le traitement de vos données personnelles sont régis par notre 
              <Link to="/confidentialite" className="text-sky-600 hover:underline ml-1">Politique de Confidentialité</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Modification des CGU</h2>
            <p className="text-slate-600 leading-relaxed">
              BOD CORP se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront 
              informés des modifications par email ou via le service. La poursuite de l'utilisation du service 
              après modification vaut acceptation des nouvelles CGU.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Résiliation</h2>
            <p className="text-slate-600 leading-relaxed">
              L'utilisateur peut résilier son compte à tout moment depuis les paramètres de son compte ou en 
              contactant le support.<br /><br />
              BOD CORP se réserve le droit de suspendre ou résilier un compte en cas de violation des présentes CGU, 
              sans préavis ni indemnité.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Droit applicable et juridiction</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes CGU sont soumises au droit français. En cas de litige, et après tentative de résolution 
              amiable, les tribunaux de Paris seront seuls compétents.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question relative aux présentes CGU, contactez-nous à : 
              <ObfuscatedEmail linkClassName="text-sky-600 hover:underline ml-1" />
            </p>
          </section>

          <p className="text-sm text-slate-400 pt-4 border-t border-slate-200">
            Dernière mise à jour : Février 2026
          </p>
        </div>
      </div>
    </div>
  );
}
